import { api } from '@/shared/lib/api'
import type { NotificationEmail, EmailTemplateInfo } from '../types'
import type { Config } from '@/modules/config/types'

export const emailService = {
  /**
   * Get notification emails from the config endpoint.
   * Returns them as a structured array.
   */
  getNotificationEmails: async (): Promise<NotificationEmail[]> => {
    const { data } = await api.get<Config>('/config_for_company/')
    const emailMap = data.list_notification_emails ?? {}
    return Object.entries(emailMap).map(([key, email]) => ({ key, email }))
  },

  /**
   * Get the list of available email templates from the backend.
   */
  getTemplates: async (): Promise<EmailTemplateInfo[]> => {
    const { data } = await api.get<EmailTemplateInfo[]>('/emails/templates/')
    return data
  },

  /**
   * Send a test/preview email for a given template to the specified address.
   * The backend renders the template with dummy data and sends it.
   */
  sendTestTemplate: async (
    templateId: string,
    destinationEmail: string,
  ): Promise<string> => {
    const { data } = await api.post<{ message: string }>(
      `/emails/test/${templateId}/`,
      { destination_email: destinationEmail },
    )
    return data.message ?? 'Email enviado'
  },

  /**
   * Get the rendered HTML preview of an email template (with dummy data).
   * Opens the HTML in a new browser tab.
   */
  openPreview: async (templateId: string): Promise<void> => {
    const { data } = await api.get<string>(`/emails/preview/${templateId}/`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
    })
    const blob = new Blob([data], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    // Revoke after a short delay so the browser has time to load it
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  },

  /**
   * Get the email feature flags from config.
   * Returns a map of template_id -> enabled (boolean).
   */
  getEmailFlags: async (): Promise<Record<string, boolean>> => {
    const { data } = await api.get<Config>('/config_for_company/')
    return data.email_flags ?? {}
  },

  /**
   * Update a single email flag. Merges with existing flags on the backend.
   */
  updateEmailFlag: async (
    templateId: string,
    enabled: boolean,
    currentFlags: Record<string, boolean>,
  ): Promise<Record<string, boolean>> => {
    const updatedFlags = { ...currentFlags, [templateId]: enabled }
    const { data } = await api.put<Config>('/config_for_company/', {
      email_flags: updatedFlags,
    })
    return data.email_flags ?? updatedFlags
  },
}
